import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import DatePicker, { registerLocale } from 'react-datepicker';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaFileExport, FaFileImport, FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { hr } from 'date-fns/locale';
import { AuthContext } from '../config/AuthProvider';
import { useLoading } from '../config/LoadingContext';
import Login from '../authPage/Login';
import { db } from '../config/firebase';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import '../App.css';
import 'react-datepicker/dist/react-datepicker.css';
import '../css/Praznici.css';

registerLocale('hr', hr);

const MONTH_NAMES_LONG = [
  'Siječanj',
  'Veljača',
  'Ožujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
];

function pad2(v) {
  return String(v).padStart(2, '0');
}

function makeDateKey(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function parseDateCell(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  if (typeof raw === 'number') {
    const utcDays = Math.floor(raw - 25569);
    const utcValue = utcDays * 86400;
    const dateInfo = new Date(utcValue * 1000);
    return Number.isNaN(dateInfo.getTime()) ? null : dateInfo;
  }
  const str = String(raw).trim();
  if (!str) return null;

  const m = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    const d = new Date(year, month - 1, day);
    if (
      d.getFullYear() === year &&
      d.getMonth() === month - 1 &&
      d.getDate() === day
    ) {
      return d;
    }
    return null;
  }

  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function Praznici() {
  const { currentUser } = useContext(AuthContext);
  const { showLoading, hideLoading } = useLoading();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formState, setFormState] = useState({ date: null, name: '' });

  const fetchHolidays = useCallback(async () => {
    showLoading('Učitavanje praznika...');
    try {
      const q = query(
        collection(db, 'holidays'),
        where('year', '==', selectedYear)
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHolidays(rows);
    } catch (e) {
      console.error('Greška pri dohvaćanju praznika:', e);
      alert('Greška pri dohvaćanju praznika.');
    } finally {
      hideLoading();
    }
  }, [hideLoading, selectedYear, showLoading]);

  useEffect(() => {
    if (currentUser) fetchHolidays();
  }, [currentUser, fetchHolidays]);

  const sortedHolidays = useMemo(() => {
    return [...holidays].sort((a, b) => {
      if (a.month !== b.month) return a.month - b.month;
      if (a.day !== b.day) return a.day - b.day;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [holidays]);

  const openAddModal = () => {
    setEditingHoliday(null);
    setFormState({ date: null, name: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setFormState({
      date: new Date(holiday.year, holiday.month - 1, holiday.day),
      name: holiday.name || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingHoliday(null);
    setFormState({ date: null, name: '' });
  };

  const handleSaveHoliday = async () => {
    const name = formState.name.trim();
    const dateObj = formState.date;
    if (!name || !dateObj) {
      alert('Unesite naziv i datum praznika.');
      return;
    }
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      alert('Datum nije ispravan.');
      return;
    }
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    if (!year || !month || !day) {
      alert('Datum nije ispravan.');
      return;
    }
    const payload = {
      year,
      month,
      day,
      dateKey: makeDateKey(year, month, day),
      name,
    };
    showLoading(editingHoliday ? 'Spremanje promjene...' : 'Dodavanje praznika...');
    try {
      if (editingHoliday) {
        await updateDoc(doc(db, 'holidays', editingHoliday.id), payload);
      } else {
        await addDoc(collection(db, 'holidays'), payload);
      }
      closeModal();
      fetchHolidays();
    } catch (e) {
      console.error('Greška pri spremanju praznika:', e);
      alert('Spremanje nije uspjelo.');
    } finally {
      hideLoading();
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    if (!window.confirm(`Izbrisati praznik "${holiday.name}" (${holiday.day}.${holiday.month}.${holiday.year})?`)) return;
    showLoading('Brisanje praznika...');
    try {
      await deleteDoc(doc(db, 'holidays', holiday.id));
      fetchHolidays();
    } catch (e) {
      console.error('Greška pri brisanju praznika:', e);
      alert('Brisanje nije uspjelo.');
    } finally {
      hideLoading();
    }
  };

  const handleExportExcel = async () => {
    showLoading('Izvoz praznika u Excel...');
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(`Praznici ${selectedYear}`);
      ws.addRow([`Praznici za godinu ${selectedYear}`]);
      ws.mergeCells(1, 1, 1, 4);
      ws.getRow(1).font = { bold: true, size: 12 };
      ws.addRow([]);
      ws.addRow(['Datum', 'Naziv praznika', 'Mjesec', 'Dan']);
      ws.getRow(3).font = { bold: true };
      sortedHolidays.forEach((h) => {
        ws.addRow([`${pad2(h.day)}.${pad2(h.month)}.${h.year}.`, h.name, MONTH_NAMES_LONG[h.month - 1], h.day]);
      });
      ws.columns = [
        { width: 16 },
        { width: 44 },
        { width: 18 },
        { width: 10 },
      ];
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `Praznici_${selectedYear}.xlsx`
      );
    } catch (e) {
      console.error('Greška pri izvozu praznika:', e);
      alert('Izvoz nije uspio.');
    } finally {
      hideLoading();
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    showLoading('Uvoz praznika iz Excela...');
    try {
      const wb = new ExcelJS.Workbook();
      const arrayBuffer = await file.arrayBuffer();
      await wb.xlsx.load(arrayBuffer);
      const ws = wb.worksheets[0];
      if (!ws) {
        alert('Excel nema list.');
        return;
      }

      const existingKeys = new Set(
        holidays.map((h) => `${h.dateKey || makeDateKey(h.year, h.month, h.day)}_${(h.name || '').toLowerCase()}`)
      );
      const toCreate = [];
      let skippedOtherYear = 0;

      ws.eachRow((row, rowNum) => {
        if (rowNum <= 1) return;
        const rawDate = row.getCell(1).value?.result ?? row.getCell(1).value;
        const rawName = row.getCell(2).value?.result ?? row.getCell(2).value;
        const dateObj = parseDateCell(rawDate);
        const name = String(rawName ?? '').trim();
        if (!dateObj || !name) return;

        const year = dateObj.getFullYear();
        const month = dateObj.getMonth() + 1;
        const day = dateObj.getDate();
        if (year !== selectedYear) {
          skippedOtherYear += 1;
          return;
        }
        const dateKey = makeDateKey(year, month, day);
        const dedupKey = `${dateKey}_${name.toLowerCase()}`;
        if (existingKeys.has(dedupKey)) return;
        existingKeys.add(dedupKey);
        toCreate.push({ year, month, day, dateKey, name });
      });

      for (const item of toCreate) {
        await addDoc(collection(db, 'holidays'), item);
      }

      const msg = `Uvezeno praznika: ${toCreate.length}${skippedOtherYear ? `, preskočeno druge godine: ${skippedOtherYear}` : ''}.`;
      alert(msg);
      fetchHolidays();
    } catch (e) {
      console.error('Greška pri uvozu praznika:', e);
      alert('Uvoz nije uspio. Provjerite format (stupac A datum, stupac B naziv).');
    } finally {
      hideLoading();
    }
  };

  if (!currentUser) return <Login />;

  return (
    <div className="praznici-page">
      <div className="filter-form">
        <div className="filter-controls">
          <label>Godina:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {Array.from({ length: 14 }, (_, i) => 2022 + i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="toolbar-buttons">
          <button type="button" className="add-person-button" onClick={openAddModal}>
            <FaPlus />
            <span>Dodaj praznik</span>
          </button>
          <button type="button" className="export-excel-button" onClick={handleExportExcel}>
            <FaFileExport />
            <span>Izvezi praznike</span>
          </button>
          <label className="manage-firms-button praznici-import-btn">
            <FaFileImport />
            <span>Uvezi praznike</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              hidden
            />
          </label>
        </div>
      </div>

      <div className="table-container praznici-table-wrap">
        <table className="praznici-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Naziv</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {sortedHolidays.length === 0 ? (
              <tr>
                <td colSpan={3} className="praznici-empty">
                  Nema praznika za odabranu godinu.
                </td>
              </tr>
            ) : (
              sortedHolidays.map((h) => (
                <tr key={h.id}>
                  <td>{pad2(h.day)}.{pad2(h.month)}.{h.year}.</td>
                  <td>{h.name}</td>
                  <td className="praznici-actions-cell">
                    <button
                      type="button"
                      className="hours-action-btn"
                      onClick={() => openEditModal(h)}
                      title="Uredi praznik"
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="hours-action-btn hours-action-btn-danger"
                      onClick={() => handleDeleteHoliday(h)}
                      title="Izbriši praznik"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel={editingHoliday ? 'Uredi praznik' : 'Dodaj praznik'}
        className="modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <h2>{editingHoliday ? 'Uredi praznik' : 'Dodaj praznik'}</h2>
          <button onClick={closeModal} className="close-modal-button">×</button>
        </div>
        <div className="praznici-modal-form">
          <label>
            Datum:
            <DatePicker
              selected={formState.date}
              onChange={(value) => setFormState((prev) => ({ ...prev, date: value }))}
              dateFormat="dd/MM/yyyy"
              locale="hr"
              calendarStartDay={1}
              placeholderText="dd/mm/yyyy"
              className="praznici-date-picker"
              showPopperArrow={false}
            />
          </label>
          <label>
            Naziv praznika:
            <input
              type="text"
              value={formState.name}
              onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="npr. Božić"
            />
          </label>
          <div className="button-group">
            <button type="button" className="save-button" onClick={handleSaveHoliday}>
              Spremi
            </button>
            <button type="button" className="cancel-button" onClick={closeModal}>
              Odustani
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Praznici;
